import { createEmptyEditorJob, getJobDetails, getMarketPlaces } from "@/modules/editor-v2/services/Jobs";
import { useQuery } from "@tanstack/react-query";
import styles from './styles.module.css';
import { memo, useMemo } from "react";
import { getFeaturedTemplates } from "@/modules/editor-v2/services/MyWorks";
import HorizontalList, { IHorizontalListItem } from "../HorizontalList";
import { useSetAtom, useAtomValue } from "jotai";
import { shouldResizeAtom, toAddBackgroundAtom, toAddBackgroundIdAtom, toAddBackgroundObjectAtom, segmentEventPayload, activeProjectAtom } from "@/modules/editor-v2/Atoms";
import useOtherCategory from "@/hooks/useOtherCategory";
import useToast from "@/hooks/useToast";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { TrackWebappEvent } from "src/modules/segment-event-tracking/segmentEventTracking";
import { EventTrackName } from "src/modules/segment-event-tracking/constant";
import useAuth from "@/hooks/useAuth";
import { Skeleton } from "@mui/material";
import { css } from "@emotion/react";

function FeaturedTemplates() {
    const {data, isLoading:loading, isError} = useQuery(
        ["featured-templates"],
        getFeaturedTemplates
    );
    const setToAddBackgroundId = useSetAtom(toAddBackgroundIdAtom);
    const setToAddBackgroundObject = useSetAtom<any>(toAddBackgroundObjectAtom);
    const setToAddBackground = useSetAtom(toAddBackgroundAtom);
    const setToResizeAtom = useSetAtom<any>(shouldResizeAtom);
    const setActiveProject = useSetAtom<any>(activeProjectAtom);
    const {otherCategory, otherSubCategory} = useOtherCategory();
    const toast = useToast();
    const router = useRouter();
    const strapiData:any = useSelector<any>(state=>state.strapi?.entireStrapiData?.WebappHomepageV22?.WebappHomepageV22);
    const items:IHorizontalListItem[] = useMemo(()=>data?.map(item=>({
            image:item?.value.thumbnail,
            extraData: item
    })),[data]);
    const getSegmentPayloadAtom = useAtomValue(segmentEventPayload);
    const templateTrigger = (template, template_name)=>{
        const eventPayload = {
            ...getSegmentPayloadAtom,
            template_id: template?._id,
            template_name: template_name,
          };
        TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_featured_template_hp}`, eventPayload);
    }

    const onItemClick = async ({extraData: {value:template, label: template_name}}) => {
        if(!otherCategory || !otherSubCategory) return toast.error("Unable to fetch categories");
        toast.info("Initializing job....");
        try {
            const jobData = await createEmptyEditorJob(otherCategory._id, otherSubCategory._id);
            const jobId = jobData?.job_id;
            setToAddBackgroundId(template._id)
            if(template.editorJSONObject.data) {
                setToAddBackgroundObject(template.editorJSONObject.data);
                setToResizeAtom(template.editorJSONObject.data.size);
            }
            else setToAddBackground(template.editorThumbnail);
            const project = await getJobDetails(jobId);
            if(!project || !project?.job_id) throw new Error("Invalid Project ID");
            setActiveProject(project);
            router.push("/dashboard/editor/"+jobId+"?backgrounds");
            templateTrigger(template, template_name)
        }
        catch(e) {
            toast.error("Unable to fetch categories");
        }
    }

    return <div className={styles.container}>
        <div className={styles.heading}>
            {strapiData?.FeaturedTemplatesSection || "Showcase your products with creatives"}
        </div>
        {
            loading 
            ? 
                <div style={inLineStyle.loaderContainer}>
                    {Array(8).fill(0)?.map((_) => (
                        <Skeleton 
                            animation={'wave'} 
                            variant={"rectangular"} 
                            width={450} 
                            height={200} 
                            style={{marginRight:6, borderRadius:0}} 
                        />
                    ))}
                    {/* @ts-ignore */}
                    <div css={inLineStyle.arrowContainer} style={inLineStyle.extraArrowStyles}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M8 4L16 12L8 20" stroke="#262626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                </div> 
            : 
                <div>
                    <HorizontalList items={items} onItemClick={onItemClick} />
                </div>
        }
    </div>
}

const inLineStyle = {
    arrowContainer : css`
        box-shadow: 0px 0px 16px 0px #0000003D;
        width: 40px;
        height: 40px;
        padding: 8px, 8px, 8px, 8px;
        border-radius: 1000px;
        background-color:white;
    `,
    extraArrowStyles: {
        right:0, 
        position:'absolute', 
        top:'50%', 
        transform:"translate(25%, -50%)", 
        zIndex:1, 
        cursor:'pointer', 
        display:'flex', 
        justifyContent:'center', 
        alignItems:"center"
    },
    loaderContainer:{
        display:'flex', 
        FlexDirection:'row', 
        Position:'relative'
    }
}

export default memo(FeaturedTemplates);